import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Calendar, User, ArrowRight, Search, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";

export default function BlogsList() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    api.get("/blogs")
      .then((r) => {
        setBlogs(r.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching blogs:", err);
        setError("Failed to load blogs. Please ensure the backend is running.");
        setLoading(false);
      });
  }, []);

  const filteredBlogs = useMemo(() => {
    if (!searchQuery) return blogs;
    const query = searchQuery.toLowerCase();
    return blogs.filter(
      (blog) =>
        blog.title.toLowerCase().includes(query) ||
        blog.extract.toLowerCase().includes(query) ||
        blog.author.toLowerCase().includes(query)
    );
  }, [blogs, searchQuery]);

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (e) {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50">
      {/* Banner Section */}
      <div className="bg-gradient-to-r from-slate-900 to-indigo-950 text-white py-12 px-4 sm:px-6 lg:px-8 border-b border-indigo-900 shadow-md">
        <div className="max-w-7xl mx-auto text-center sm:text-left">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/20 text-indigo-300 text-xs font-semibold uppercase tracking-wider mb-3">
            <BookOpen className="w-3.5 h-3.5" />
            Knowledge Hub
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">SIF Insights & Blogs</h1>
          <p className="mt-3 text-sm sm:text-base text-slate-300 max-w-2xl font-medium">
            Expert commentary, regulatory deep dives, and data-driven analysis of Specialised Investment Funds (SIFs) in India.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Toolbar */}
        <div className="bg-white rounded-xl border border-sif p-4 card-shadow mb-8">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-sif-muted" />
            <input
              type="text"
              placeholder="Search blogs by title, content, or author..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 rounded-lg border border-sif bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-sif-primary/20 focus:bg-white transition"
            />
          </div>
        </div>

        {/* Loading skeleton */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((n) => (
              <div key={n} className="bg-white rounded-2xl border border-sif overflow-hidden card-shadow animate-pulse">
                <div className="h-48 bg-slate-200" />
                <div className="p-5 space-y-4">
                  <div className="flex justify-between items-center">
                    <div className="h-4 w-24 bg-slate-200 rounded" />
                    <div className="h-4 w-16 bg-slate-200 rounded" />
                  </div>
                  <div className="h-6 w-3/4 bg-slate-200 rounded" />
                  <div className="space-y-2">
                    <div className="h-4 bg-slate-200 rounded" />
                    <div className="h-4 w-5/6 bg-slate-200 rounded" />
                  </div>
                  <div className="h-8 w-28 bg-slate-200 rounded pt-2" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-xl flex items-start gap-3 my-8 card-shadow">
            <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-800">Connection Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && filteredBlogs.length === 0 && (
          <div className="text-center py-16 bg-white border border-sif rounded-2xl card-shadow">
            <BookOpen className="w-12 h-12 text-slate-300 mx-auto stroke-1" />
            <h3 className="mt-4 font-bold text-slate-800 text-lg">No blogs found</h3>
            <p className="text-slate-500 text-sm mt-1 max-w-sm mx-auto">
              We couldn't find any articles matching your search query. Try typing something else.
            </p>
          </div>
        )}

        {/* Blogs Grid */}
        {!loading && !error && filteredBlogs.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBlogs.map((blog) => (
              <article
                key={blog.slug}
                className="group bg-white rounded-2xl border border-sif overflow-hidden card-shadow hover:shadow-lg transition-all duration-300 flex flex-col justify-between"
              >
                <div>
                  {/* Thumbnail Cover Image */}
                  <Link to={`/blogs/${blog.slug}`} className="block overflow-hidden relative h-48 sm:h-52 bg-slate-100 border-b border-sif">
                    <img
                      src={blog.image}
                      alt={blog.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </Link>

                  {/* Metadata and Title */}
                  <div className="p-5">
                    <div className="flex items-center justify-between text-xs text-sif-muted mb-3 font-semibold">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-indigo-500" />
                        <span>{formatDate(blog.date)}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5 text-indigo-500" />
                        <span>{blog.author}</span>
                      </div>
                    </div>

                    <Link to={`/blogs/${blog.slug}`} className="block">
                      <h2 className="text-lg font-bold text-slate-900 group-hover:text-indigo-600 transition-colors leading-snug line-clamp-2">
                        {blog.title}
                      </h2>
                    </Link>

                    <p className="mt-3 text-slate-600 text-sm leading-relaxed line-clamp-3">
                      {blog.extract}
                    </p>
                  </div>
                </div>

                {/* Footer read action */}
                <div className="px-5 pb-5 pt-2 mt-auto">
                  <Link
                    to={`/blogs/${blog.slug}`}
                    className="inline-flex items-center gap-1 text-sm font-bold text-indigo-600 hover:text-indigo-800 transition"
                  >
                    <span>Read Full Article</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
